import { useMemo } from 'react';
import { Chart } from '@tanstack/react-charts';
import { defineChart, lineY, areaY } from '@tanstack/charts';
import { scaleLinear } from '@tanstack/charts/scales/linear';
import { scalePoint } from '@tanstack/charts/scales/point';
import Dot from '@/components/ui/Dot.tsx';
import { cn } from '@/lib/utils.ts';

export interface LineChartPoint {
  /** Etykieta na osi poziomej — tydzień, miesiąc. */
  label: string;
  value: number;
}

export interface LineChartSeries {
  id: string;
  /** Napis w legendzie. */
  label: string;
  color: string;
  points: LineChartPoint[];
  /** Linia przerywana — prognoza, wartość niepewna. */
  dashed?: boolean;
  /** Wypełnienie pod linią — seria główna, gdy ma dominować. */
  filled?: boolean;
  /** Grubsza kreska dla serii, którą czyta się pierwszą. */
  emphasized?: boolean;
}

interface LineChartProps extends React.HTMLAttributes<HTMLDivElement> {
  series: LineChartSeries[];
  height?: number;
  /** Sufit osi pionowej — limit miejsc, żeby wykres nie kończył się na maksimum danych. */
  maxValue?: number | null;
  ariaLabel: string;
}

/**
 * Wykres liniowy — jedyne miejsce w aplikacji, które zna `@tanstack/charts`
 * po stronie linii.
 *
 * Biblioteka jest przed wersją 1.0 i zmienia API z tygodnia na tydzień, więc
 * ekrany dostają własne, wąskie propsy: serie, barwy, wysokość. Kiedy API się
 * ruszy, poprawka jest w tym pliku, a nie w każdej karcie.
 */
const LineChart = ({
  series,
  height = 220,
  maxValue,
  ariaLabel,
  className,
  ...props
}: LineChartProps) => {
  const definition = useMemo(() => {
    // Oś kategorii musi znać wszystkie etykiety, także te, które ma tylko
    // prognoza — inaczej jej punkty wypadają poza dziedzinę i znikają.
    const labels = [
      ...new Set(
        series.flatMap((line) => line.points.map((point) => point.label)),
      ),
    ];

    const highest = Math.max(
      ...series.flatMap((line) => line.points.map((point) => point.value)),
      0,
    );
    const ceiling = maxValue ?? highest;

    return defineChart({
      marks: series.flatMap((line) => {
        const rows = line.points.map((point) => ({
          label: point.label,
          value: point.value,
        }));

        const stroke = lineY(rows, {
          x: 'label',
          y: 'value',
          stroke: line.color,
          strokeWidth: line.emphasized ? 2.5 : 1.75,
          strokeDasharray: line.dashed ? '5 4' : undefined,
          points: true,
        });

        return line.filled
          ? [
              areaY(rows, {
                x: 'label',
                y: 'value',
                fill: line.color,
                fillOpacity: 0.12,
              }),
              stroke,
            ]
          : [stroke];
      }),
      x: { scale: () => scalePoint<string>().domain(labels).padding(0.15) },
      y: {
        scale: () => scaleLinear().domain([0, ceiling || 1]),
        nice: true,
        grid: true,
      },
    });
  }, [series, maxValue]);

  return (
    <div className={cn('flex flex-col gap-3', className)} {...props}>
      <Chart definition={definition} height={height} ariaLabel={ariaLabel} />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {series.map((line) => (
          <span
            key={line.id}
            className="inline-flex items-center gap-2 text-[13px] text-neutral-700"
          >
            <Dot size="md" style={{ backgroundColor: line.color }} />
            {line.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default LineChart;
